import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged, Subscription, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/authentication/auth.service';
import { FsTicketsAuthService } from '../../../services/fs-tickets/fs-tickets-auth.service';
import { BidderCommunityMenuService } from '../../../services/bidder-community/bidder-community-menu/bidder-community-menu.service';

@Component({
  selector: 'app-my-tickets',
  standalone: true,
  imports: [],
  templateUrl: './my-tickets.component.html',
  styleUrl: './my-tickets.component.scss'
})
export class MyTicketsComponent implements OnInit, OnDestroy {
  @ViewChild('fsIframe') private iframeRef!: ElementRef<HTMLIFrameElement>;

  iframeUrl!: SafeResourceUrl;
  iframeHeight = signal(400);

  private readonly fsOrigin = environment.fsTicketsOrigin;
  private iframeReady = false;
  private currentToken = '';
  private authSubscription!: Subscription;
  private tokenSubscription!: Subscription;
  private readonly messageHandler = (event: MessageEvent) => this.onMessage(event);

  private readonly sanitizer = inject(DomSanitizer);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly fsTicketsAuthService = inject(FsTicketsAuthService);
  private readonly bidderCommunityMenuService = inject(BidderCommunityMenuService);
  private readonly zone = inject(NgZone);

  isAuthenticated = this.oidcSecurityService.authenticated;

  ngOnInit(): void {
    setTimeout(() => {
      this.bidderCommunityMenuService.setPageData({
        id: 0,
        pageName: 'My Tickets',
        pageRoute: 'my-tickets'
      });

      if (typeof window !== 'undefined') {
        window.onpageshow = function (event) {
          if (event.persisted) {
            window.location.reload();
          }
        };

        if (!this.isAuthenticated().isAuthenticated) {
          window.open(environment.mediaUrl, '_self');
          return;
        }

        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `${environment.fsTicketsOrigin}/my-tickets`
        );

        this.tokenSubscription = this.oidcSecurityService.getAccessToken()
          .pipe(distinctUntilChanged())
          .subscribe(token => {
            if (!token) return;
            const prev = this.currentToken;
            this.currentToken = token;
            if (this.iframeReady && prev && prev !== token) {
              this.postAuthToken(token);
            }
          });

        window.addEventListener('message', this.messageHandler);
      }

      this.authSubscription = this.authService.isAuthenticated$
        .pipe(distinctUntilChanged())
        .subscribe(isAuth => {
          if (!isAuth && typeof window !== 'undefined') {
            window.open(environment.mediaUrl, '_self');
          }
        });
    }, 100);
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.messageHandler);
    }
    this.authSubscription?.unsubscribe();
    this.tokenSubscription?.unsubscribe();
  }

  private onMessage(event: MessageEvent): void {
    if (event.origin !== this.fsOrigin) return;
    const msg = event.data;
    switch (msg?.type) {
      case 'FS_READY':
        this.zone.run(() => {
          this.iframeReady = true;
          this.postAuthToken(this.currentToken);
        });
        break;
      case 'FS_HEIGHT_CHANGE':
        if (typeof msg.height === 'number') {
          this.zone.run(() => this.iframeHeight.set(msg.height));
        }
        break;
      case 'FS_SESSION_EXPIRED':
        this.fsTicketsAuthService.refreshAccessToken().pipe(take(1)).subscribe(token => {
          this.zone.run(() => {
            if (token) {
              this.currentToken = token;
              this.postAuthToken(token);
            } else {
              window.open(environment.mediaUrl, '_self');
            }
          });
        });
        break;
    }
  }

  private postAuthToken(token: string): void {
    if (!token) return;
    this.iframeRef?.nativeElement?.contentWindow?.postMessage(
      { type: 'FS_AUTH_TOKEN', sessionToken: token, actorType: 'customer_user' },
      this.fsOrigin
    );
  }
}
